import {
  collection,
  doc,
  onSnapshot,
  query,
  getDoc,
} from "firebase/firestore";
import { useEffect, useState, useCallback } from "react";
import { auth } from "@/api/firebase";
import { db } from "@/api/firestore";
import { createProtoFromFirestore } from "@/lib/proto";
import { ThreadSchema } from "@generated/proto/model";
import type { Thread } from "@generated/proto/model";

interface ThreadRow {
  id: string;
  proto: Thread;
  created?: Date;
  modified?: Date;
}

export function useThreadFirestore(tripId?: string) {
  const [threads, setThreads] = useState<ThreadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tripId) {
      setThreads([]);
      setLoading(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Create Firestore query for threads
    const threadsRef = collection(
      db, 
      `users/${user.uid}/trips/${tripId}/threads`
    );
    const q = query(threadsRef);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        try {
          const data = snapshot.docs.map((doc) => {
            const raw: any = doc.data();
            const proto = createProtoFromFirestore<Thread>(ThreadSchema, raw);
            return {
              id: doc.id,
              proto,
              created: raw.created?.toDate?.(),
              modified: raw.modified?.toDate?.(),
            } satisfies ThreadRow;
          });
          setThreads(data);
          setLoading(false);
        } catch (err: any) {
          setError("Failed to load threads");
          console.error("[useThreadFirestore] error parsing threads:", err);
          setLoading(false);
        }
      },
      (err) => {
        setError("Firestore subscription failed");
        console.error("[useThreadFirestore] subscription error:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tripId]);

  // Get a specific thread by ID
  const getThread = useCallback(async (threadId: string): Promise<Thread | null> => {
    if (!tripId || !threadId) return null;

    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }

    try {
      const threadRef = doc(
        db, 
        `users/${user.uid}/trips/${tripId}/threads/${threadId}`
      );
      const threadDoc = await getDoc(threadRef);
      
      if (!threadDoc.exists()) {
        return null;
      }

      const raw = threadDoc.data();
      return createProtoFromFirestore<Thread>(ThreadSchema, raw);
    } catch (err: any) {
      console.error("[useThreadFirestore] error getting thread:", err);
      throw err;
    }
  }, [tripId]);

  return {
    threads,
    loading,
    error,
    getThread,
  };
}